import { DEBUG } from 'app/auth/authRoles'
import { ReduxUser } from 'app/redux/ReduxInterfaces'
import sfOauthConfig from 'app/services/sfAuth/sfAuthConfig'
import { ConnectedMap } from './FormTypes'

export const getObjectsIdsFromConnectedMap = (connectedMap: ConnectedMap, mainConnectedObject: string): {
  connectedAccount: string
  connectedSurvey: string
  connectedOpportunity: string
  editedObjectId: string
  connectedResource: string
} => {
  let connectedAccount, connectedOpportunity, connectedResource, connectedSurvey, editedObjectId

  Object.values(connectedMap).forEach((obj) => {
    if (obj.objectType === 'Opportunity') {
      connectedOpportunity = obj.sfObject.Id
    }
    if (obj.objectType === 'Account') {
      connectedAccount = obj.sfObject.Id
    }
    if (obj.objectType === 'Resource__c') {
      connectedResource = obj.sfObject.Id
    }
    if (obj.objectType === 'Survey__c') {
      connectedSurvey = obj.sfObject.Id
    }
    if (obj.objectType === mainConnectedObject) {
      editedObjectId = obj.sfObject.Id
    }
  })
  return { connectedAccount, connectedOpportunity, connectedResource, connectedSurvey, editedObjectId }
}

export function shouldBlockAutoSaveForUser (user: ReduxUser): boolean {
  const isTesting = sfOauthConfig.isTesting as boolean
  const isDebug = user.role.includes(DEBUG)
  return !isTesting && isDebug
}

/**
 * Checks complex Salesforce save results for error occurrences.
 * @param result The save result(s) to inspect.
 * @returns True if any error is found.
 */
export const checkIfSavingResultHasErrors = (result: any): boolean => {
  if (Array.isArray(result)) {
    return result.some((sub) => checkIfSavingResultHasErrors(sub))
  } else {
    if (result == null) {
      console.error(
        'No promise resolution configured or an unexpected error ocurred!'
      )
      return true
    }
    if (result.compositeResponse != null) {
      return result.compositeResponse.some((comp) => {
        if (comp.body == null) {
          return false
        } else {
          return comp.body.some((sub) => sub.success === false)
        }
      })
    }
    if ('isSuccess' in result) {
      if (result.outputValues != null && 'error' in result.outputValues) {
        return Boolean(result.outputValues.error)
      }
      return result.isSuccess === false
    }
    if ('success' in result) {
      return result.success === false
    }
    if ('hasErrors' in result) {
      return result.hasErrors
    }
    return false
  }
}

const ID_KEYS = ['id', 'Id']

const isPlainObject = (value: unknown): value is Record<string, any> =>
  value != null && typeof value === 'object' && !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype

const isMissingId = (value: unknown): boolean => value == null || value === ''

/**
 * Fills ids missing in nested objects of current value with ids present in fresh value at the same path.
 * Only id keys are added, other keys are left untouched. Arrays are skipped, as index based matching
 * could assign an id of a different record. Unchanged branches keep their references.
 * @param current Current value of the form element.
 * @param fresh Value of the form element calculated from freshly fetched data.
 * @param isRoot Whether current is the root value of the form element.
 * @returns Current value with filled ids.
 */
const fillMissingNestedIds = (current: unknown, fresh: unknown, isRoot: boolean): unknown => {
  if (!isPlainObject(current) || !isPlainObject(fresh)) {
    return current
  }
  let result = current
  const assign = (key: string, value: unknown): void => {
    if (result === current) {
      result = { ...current }
    }
    result[key] = value
  }
  // Root value of an element without id can be a deliberately selected new record (e.g. in pickers),
  // so ids are filled only for nested objects, which represent child records of the element
  if (!isRoot) {
    ID_KEYS.forEach((key) => {
      if (isMissingId(current[key]) && typeof fresh[key] === 'string' && fresh[key] !== '') {
        assign(key, fresh[key])
      }
    })
  }
  Object.keys(current).forEach((key) => {
    if (ID_KEYS.includes(key)) {
      return
    }
    const filled = fillMissingNestedIds(current[key], fresh[key], false)
    if (filled !== current[key]) {
      assign(key, filled)
    }
  })
  return result
}

/**
 * Fills ids of records created by a partially failed save into current form values, without overwriting any user input.
 * Prevents creating duplicated records when the form is saved again.
 * @param currentValues Current form values.
 * @param freshValues Initial values calculated from freshly fetched data.
 * @returns Current form values with filled ids.
 */
export const fillMissingIdsInFormValues = (
  currentValues: Record<string, any>,
  freshValues: Record<string, any>
): Record<string, any> => {
  let result = currentValues
  Object.keys(currentValues).forEach((key) => {
    const filled = fillMissingNestedIds(currentValues[key], freshValues[key], true)
    if (filled !== currentValues[key]) {
      if (result === currentValues) {
        result = { ...currentValues }
      }
      result[key] = filled
    }
  })
  return result
}
